import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { styled } from "@ff/ui";
import Box from "@ff/ui/Box";
import Button from "@ff/ui/Button";
import { formatCurrency } from "client/utils/formatter";
import { colors, H3, P16 } from "client/v3/typography";
import Link from '@ff/ui/Link';
import Icon from '@ff/ui/Icon';
import PriceSummary from 'client/v3/components/PriceSummary';

export type SummaryProps = {
  modelCode: string;
  basePrice: number;
  totalPrice: number;
  summaryPageUrl: string;
  disableButton: boolean;
  exterior: {
    code: string;
    name: string;
    price: number
  },
  wheels: {
    code: string;
    name: string;
    price: number
  },
  interior: {
    code: string;
    name: string;
    price: number;
  },
  visible: boolean;
}

export default function Summary(props: SummaryProps){
  const history = useHistory();
  const {
    modelCode,
    basePrice,
    totalPrice,
    summaryPageUrl,
    disableButton,
    exterior,
    wheels,
    interior,
    visible,
  } = props;
  const formattedPrice = formatCurrency(totalPrice);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if(!visible){
      setCollapsed(true);
    }
  }, [visible]);

  const handleContinueButtonClicked = () => {
    history.push(summaryPageUrl);
  }
  
  const handleDetailLinkClicked = () => {
    setCollapsed(pre => !pre);
  };

  const priceSummaryProps = {
    modelCode: modelCode,
    basePrice: basePrice,
    options: [
      {
        categoryName: 'Exterior',
        ...exterior,
      },
      {
        categoryName: 'Wheels',
        ...wheels,
      },
      {
        categoryName: 'Interior',
        ...interior,
      },
    ],
  };

  return (
    <Container>
      <DetailContent collapsed={collapsed}>
        <PriceSummary {...priceSummaryProps}></PriceSummary>
      </DetailContent>
      <PriceContainer>
        <P16>Summary</P16>
        <H3>{formattedPrice}</H3>
      </PriceContainer>
      <DetailButton collapsed={collapsed}>
        <Link onClick={handleDetailLinkClicked}><DetailText>Details</DetailText></Link>
        <DetailArrow name={ collapsed ? 'ff-icon-keyboard_arrow_up' : 'ff-icon-keyboard_arrow_down'} />
      </DetailButton>
      <ContinueButton variant='primary' onClick={handleContinueButtonClicked} disabled={disableButton} >
        <ButtonText>Continue</ButtonText>
      </ContinueButton>
    </Container>
  );
}

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  padding: '32px 52px',
  backgroundColor: colors.background4,
  borderTop: colors.border5,
  backdropFilter: 'blur(11px)',
  width: 'inherit',
});

const PriceContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexBasis: '52px',
});

const ContinueButton = styled(Button)(({ disabled }) => ({
  width: '100%',
  alignSelf: 'center',
  border: disabled ? 'unset': 'none',
  padding: '10px 16px',
}));

const ButtonText = styled(P16)(({
  textTransform: 'none',
  lineHeight: '18.75px',
  textDecoration: 'none',
}));

const DetailButton = styled(Box)<{collapsed: boolean}>(({ collapsed }) => ({
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.content1,
  paddingBottom: '16px',
  paddingTop: collapsed ? '0px' : '16px',
  borderTop: collapsed ? 'none' : colors.border3,
}));

const DetailText = styled(P16)({
  textDecorationLine: 'underline',
});

const DetailArrow = styled(Icon)({
  fontSize: '20px'
});

const DetailContent = styled(Box)<{ collapsed: boolean }>(({ collapsed }) => ({
  display: collapsed ? 'none' : 'block',
  paddingBottom: '26px',  
  borderBottom: colors.border2, 
}));